'use client'

import { ArrowRight } from 'lucide-react'
import React from 'react'

import { THEME_COLORS_ENUM } from '../../../types/colors'

export interface IProgramCardHeaderProps {
	eyebrow?: string
	subtitle?: string
	title: string
	/** When set, rendered on a second line below the title (used with splitTitle). */
	titleSecondLine?: string | null
	price: number
	oldPrice?: number
	/** Header link URL. When set and no headerButtons, header is rendered as link. */
	url?: string
	/** Theme color for header link when url is set. */
	headerColor?: THEME_COLORS_ENUM
	featured?: boolean
	/** Base URL of SVG sprite for featured icon (e.g. "/assets/icons.svg"). If omitted, uses same-document fragment #s_fav_p1/#s_fav_p2. */
	iconsSpriteUrl?: string
	/** Buttons to render instead of header link arrow. When set, header is not a link. */
	headerButtons?: React.ReactNode[]
	/** Link component for header (e.g. next/link, <a>). Defaults to <a>. */
	link?: React.ElementType<React.AnchorHTMLAttributes<HTMLAnchorElement>>
}

export const ProgramCardHeader: React.FC<IProgramCardHeaderProps> = ({
	eyebrow,
	subtitle,
	title,
	titleSecondLine,
	price,
	oldPrice,
	url,
	headerColor = THEME_COLORS_ENUM.CYCLAMEN,
	featured,
	iconsSpriteUrl = '',
	headerButtons,
	link: LinkComponent
}) => {
	const hasHeaderButtons = headerButtons != null && headerButtons.length > 0
	const renderAsLink = !hasHeaderButtons && url != null && url !== ''
	const Link = LinkComponent ?? 'a'
	const favHref = (id: string) =>
		iconsSpriteUrl ? `${iconsSpriteUrl.replace(/#.*$/, '')}#${id}` : `#${id}`
	const content = (
		<>
			<div className='m-program-card__header-left'>
				{eyebrow && <span className='m-program-card__header-eyebrow'>{eyebrow}</span>}

				<h3 className='m-program-card__header-title'>
					{titleSecondLine != null && titleSecondLine !== '' ? (
						<>
							<span>{title}</span>
							<span>{titleSecondLine}</span>
						</>
					) : (
						title
					)}
				</h3>

				{subtitle && <span className='m-program-card__header-subtitle'>{subtitle}</span>}

				<div className='m-program-card__header-price-wrapper'>
					<span className='m-program-card__header-currency'>€</span>

					<span className='m-program-card__header-price'>
						{price.toFixed(2).replace('.', ',')}
					</span>

					{oldPrice !== undefined && (
						<span className='m-program-card__header-price m-program-card__header-price--old'>
							{oldPrice.toFixed(2).replace('.', ',')}
						</span>
					)}
				</div>
			</div>

			<div className='m-program-card__header-right'>
				{hasHeaderButtons ? (
					<div className='m-program-card__header-buttons'>{headerButtons}</div>
				) : (
					<div className='m-program-card__header-link-arrow'>
						<div className='m-program-card__header-link-arrow-icons'>
							<span>
								<ArrowRight size={20} />
							</span>
							<span>
								<ArrowRight size={20} />
							</span>
						</div>
					</div>
				)}
			</div>
		</>
	)

	return (
		<div className='m-program-card__header'>
			{renderAsLink ? (
				<Link
					href={url!}
					className='m-program-card__header-link'
					{...(headerColor ? { color: headerColor } : {})}
				>
					{content}
				</Link>
			) : (
				content
			)}

			{featured && (
				<div className='m-program-card__header-featured'>
					<svg
						className='m-program-card__header-featured-svg'
						viewBox='0 0 62 62'
						aria-hidden
					>
						<g>
							<use
								href={favHref('s_fav_p1')}
								className='m-program-card__header-featured-use'
							/>
						</g>
						<g className='m-program-card__header-featured-rotate'>
							<use
								href={favHref('s_fav_p2')}
								className='m-program-card__header-featured-use'
							/>
						</g>
					</svg>
				</div>
			)}
		</div>
	)
}

ProgramCardHeader.displayName = 'AProgramCardHeader'
